(function () {
  var SELECTOR = '.js-orion-auto-fit';
  var processedState = new WeakMap();
  var boundsCache = {};

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function getImageElementPixelSize(imageElement) {
    if (!imageElement) return null;
    var width = Number(imageElement.naturalWidth || imageElement.width) || 0;
    var height = Number(imageElement.naturalHeight || imageElement.height) || 0;
    if (!(width > 0) || !(height > 0)) return null;
    return { width: width, height: height };
  }

  function getWhiteBackgroundSample(pixels, imageWidth, imageHeight) {
    if (!pixels || !pixels.length || !(imageWidth > 0) || !(imageHeight > 0)) return null;

    var samplePoints = [
      [0, 0],
      [imageWidth - 1, 0],
      [0, imageHeight - 1],
      [imageWidth - 1, imageHeight - 1],
      [Math.floor(imageWidth / 2), 0],
      [Math.floor(imageWidth / 2), imageHeight - 1]
    ];

    var totalR = 0;
    var totalG = 0;
    var totalB = 0;
    var count = 0;
    var darkest = 255;

    for (var index = 0; index < samplePoints.length; index += 1) {
      var x = Math.max(0, Math.min(imageWidth - 1, samplePoints[index][0]));
      var y = Math.max(0, Math.min(imageHeight - 1, samplePoints[index][1]));
      var offset = (y * imageWidth + x) * 4;
      var alpha = pixels[offset + 3];
      if (alpha <= 8) continue;
      var r = pixels[offset];
      var g = pixels[offset + 1];
      var b = pixels[offset + 2];
      var brightness = (r + g + b) / 3;
      totalR += r;
      totalG += g;
      totalB += b;
      count += 1;
      if (brightness < darkest) darkest = brightness;
    }

    if (!count) return null;

    var sample = {
      r: totalR / count,
      g: totalG / count,
      b: totalB / count
    };
    var overallBrightness = (sample.r + sample.g + sample.b) / 3;
    if (overallBrightness < 235 || darkest < 220) return null;
    return sample;
  }

  function getVisibleBoundsFromWhiteBackgroundPixels(pixels, imageWidth, imageHeight) {
    var background = getWhiteBackgroundSample(pixels, imageWidth, imageHeight);
    if (!background) return null;

    var minX = imageWidth;
    var minY = imageHeight;
    var maxX = -1;
    var maxY = -1;

    for (var y = 0; y < imageHeight; y += 1) {
      for (var x = 0; x < imageWidth; x += 1) {
        var offset = (y * imageWidth + x) * 4;
        var alpha = pixels[offset + 3];
        if (alpha <= 8) continue;
        var r = pixels[offset];
        var g = pixels[offset + 1];
        var b = pixels[offset + 2];
        var distance = Math.abs(r - background.r) + Math.abs(g - background.g) + Math.abs(b - background.b);
        if (distance <= 36) continue;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }

    if (maxX < minX || maxY < minY) return null;

    var visibleWidth = maxX - minX + 1;
    var visibleHeight = maxY - minY + 1;
    if (!(visibleWidth > 0) || !(visibleHeight > 0)) return null;
    if (visibleWidth >= imageWidth * 0.995 && visibleHeight >= imageHeight * 0.995) return null;

    return {
      left: minX,
      top: minY,
      width: visibleWidth,
      height: visibleHeight
    };
  }

  function getVisibleBoundsFromProbeImage(imageElement) {
    var size = getImageElementPixelSize(imageElement);
    if (!size || typeof document === 'undefined') return null;

    var probeCanvas = document.createElement('canvas');
    probeCanvas.width = size.width;
    probeCanvas.height = size.height;
    var context = probeCanvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;

    try {
      context.clearRect(0, 0, size.width, size.height);
      context.drawImage(imageElement, 0, 0, size.width, size.height);
      var imageData = context.getImageData(0, 0, size.width, size.height);
      var pixels = imageData && imageData.data ? imageData.data : null;
      if (!pixels || !pixels.length) return null;

      var minX = size.width;
      var minY = size.height;
      var maxX = -1;
      var maxY = -1;

      for (var y = 0; y < size.height; y += 1) {
        for (var x = 0; x < size.width; x += 1) {
          var alpha = pixels[(y * size.width + x) * 4 + 3];
          if (alpha <= 8) continue;
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }

      if (maxX < minX || maxY < minY) return null;

      var visibleWidth = maxX - minX + 1;
      var visibleHeight = maxY - minY + 1;
      if (!(visibleWidth > 0) || !(visibleHeight > 0)) return null;

      if (visibleWidth >= size.width * 0.995 && visibleHeight >= size.height * 0.995) {
        return getVisibleBoundsFromWhiteBackgroundPixels(pixels, size.width, size.height);
      }

      return {
        left: minX,
        top: minY,
        width: visibleWidth,
        height: visibleHeight
      };
    } catch (_error) {
      return null;
    }
  }

  function loadProbeImage(source) {
    return new Promise(function (resolve) {
      if (!source) {
        resolve(null);
        return;
      }
      var image = new Image();
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.onload = function () {
        resolve(image);
      };
      image.onerror = function () {
        resolve(null);
      };
      image.src = source;
    });
  }

  function resetImageTransform(image) {
    image.style.removeProperty('--orion-auto-fit-scale');
    image.style.removeProperty('--orion-auto-fit-x');
    image.style.removeProperty('--orion-auto-fit-y');
    image.classList.remove('is-orion-auto-fit-ready');
  }

  function applyBoundsToImage(image, bounds, imageSize) {
    if (!image || !bounds || !imageSize) {
      resetImageTransform(image);
      return;
    }

    var maxScale = Number(image.getAttribute('data-orion-fit-max-scale') || 1.35);
    var targetFill = Number(image.getAttribute('data-orion-fit-target-fill') || 0.88);
    var whitespaceThreshold = Number(image.getAttribute('data-orion-fit-threshold') || 0.88);
    var fillX = bounds.width / imageSize.width;
    var fillY = bounds.height / imageSize.height;
    var minFill = Math.min(fillX, fillY);
    var maxFill = Math.max(fillX, fillY);

    if (!(minFill > 0) || maxFill >= 0.985) {
      resetImageTransform(image);
      return;
    }

    if (minFill >= whitespaceThreshold) {
      resetImageTransform(image);
      return;
    }

    var scaleForWidth = targetFill / fillX;
    var scaleForHeight = targetFill / fillY;
    var scale = Math.min(scaleForWidth, scaleForHeight);
    scale = clamp(scale, 1, maxScale);

    if (!(scale > 1.025)) {
      resetImageTransform(image);
      return;
    }

    var centerX = (bounds.left + bounds.width / 2) / imageSize.width;
    var centerY = (bounds.top + bounds.height / 2) / imageSize.height;
    var translateX = clamp(((0.5 - centerX) * 100) / scale, -12, 12);
    var translateY = clamp(((0.5 - centerY) * 100) / scale, -12, 12);

    image.style.setProperty('--orion-auto-fit-scale', String(scale));
    image.style.setProperty('--orion-auto-fit-x', translateX.toFixed(2) + '%');
    image.style.setProperty('--orion-auto-fit-y', translateY.toFixed(2) + '%');
    image.classList.add('is-orion-auto-fit-ready');
  }

  function processImage(image) {
    if (!image) return;
    var source = image.currentSrc || image.getAttribute('src') || '';
    if (!source) return;

    var previousState = processedState.get(image);
    if (previousState === source) return;
    processedState.set(image, source);

    var cached = boundsCache[source];
    if (cached) {
      applyBoundsToImage(image, cached.bounds, cached.size);
      return;
    }

    loadProbeImage(source).then(function (probeImage) {
      if (!probeImage) {
        resetImageTransform(image);
        return;
      }
      var size = getImageElementPixelSize(probeImage);
      var bounds = getVisibleBoundsFromProbeImage(probeImage);
      boundsCache[source] = { bounds: bounds, size: size };
      applyBoundsToImage(image, bounds, size);
    });
  }

  function bindImage(image) {
    if (!image || image.__orionAutoFitBound) return;
    image.__orionAutoFitBound = true;

    if (image.complete) processImage(image);
    image.addEventListener('load', function () {
      processedState.delete(image);
      processImage(image);
    });
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var images = scope.querySelectorAll(SELECTOR);
    images.forEach(function (image) {
      bindImage(image);
      processImage(image);
    });
  }

  function observeMutations() {
    if (typeof MutationObserver === 'undefined') return;
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function (node) {
            if (!node || node.nodeType !== 1) return;
            if (node.matches && node.matches(SELECTOR)) bindImage(node);
            scan(node);
          });
        }
        if (mutation.type === 'attributes') {
          var target = mutation.target;
          if (!target || !target.matches || !target.matches(SELECTOR)) return;
          processedState.delete(target);
          processImage(target);
        }
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset']
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    scan(document);
    observeMutations();
  });
})();
